import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@components/atoms/Typography';
import { Card } from '@components/atoms/Card';
import { Table } from '@components/atoms/Table';
import { InputAmount } from '@components/atoms/InputAmount';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { ProductAvailability } from '@types';
import { formatPrice } from '@utils/format';
import { useProductAvailability } from '@hooks/useProductAvailability';
import { useThemes } from '@hooks/useThemes';
import styles from './ProductQuantityCard.module.css';

export interface ProductQuantityCardProps {
    uuid?: string;
    name?: string;
    image?: string;
    price?: number;
    attributes?: Record<string, string>;
    availability?: ProductAvailability;
    quantity: number;
    setQuantity: (value: number) => void;
}

export const ProductQuantityCard: FC<ProductQuantityCardProps> = ({
    uuid,
    name,
    image,
    price,
    quantity,
    attributes,
    availability,
    setQuantity,
}) => {
    const { useGrayscaleTheme } = useThemes();
    const { translateAvailability, getAvailabilityTypographyColor } = useProductAvailability();
    const { t } = useTranslation();

    const translations = {
        productLabel: t('product-table-component.product.label'),
        quantityLabel: t('product-table-component.quantity.label'),
        priceLabel: t('product-table-component.price.label'),
    };

    return (
        <div className={`${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`}>
            <Card>
                <Table
                    borderless={true}
                    hideHeaderOnMobile={true}
                    headerCols={[
                        { classes: 'text-start', text: translations.productLabel },
                        { classes: 'text-center', text: translations.quantityLabel, fit: true },
                        { classes: `${styles['price-col']} text-end`, text: translations.priceLabel },
                    ]}
                    contentRows={[
                        [
                            {
                                content: (
                                    <>
                                        <div className="d-flex gap-3 align-items-start text-decoration-none my-4 mb-3 mb-md-4">
                                            <div>
                                                {image && <img className={styles['product-image']} src={image} alt={name} />}
                                            </div>
                                            <div className="d-grid">
                                                <Anchor
                                                    href={SITE_URLS.productDetail.replace(':uuid', `${uuid}`)}
                                                    mode={AnchorMode.none}>
                                                    <Typography
                                                        classes="mt-2 mb-1 pt-1"
                                                        color="dark"
                                                        fontSize="14"
                                                        fontWeight="900"
                                                        breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                        {name}
                                                    </Typography>
                                                </Anchor>
                                                {availability !== undefined && (
                                                    <Typography
                                                        classes="text-truncate mb-1"
                                                        color={getAvailabilityTypographyColor(availability)}
                                                        fontSize="14">
                                                        {translateAvailability(availability)}
                                                    </Typography>
                                                )}
                                                {attributes &&
                                                    Object.entries(attributes).map(([key, value]) => (
                                                        <Typography
                                                            key={key}
                                                            classes="mb-1"
                                                            color="dark"
                                                            fontSize="14"
                                                            fontWeight="400"
                                                            breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                            {value}
                                                        </Typography>
                                                    ))}
                                            </div>
                                        </div>
                                        <div className="d-flex d-md-none justify-content-between align-items-center me-3 my-3">
                                            <div className="d-flex gap-3">
                                                <ProductAmount
                                                    quantity={quantity || 0}
                                                    setQuantity={(value) => setQuantity(value)}
                                                />
                                            </div>
                                            <Typography
                                                classes={`${styles.price} mb-0`}
                                                align="right"
                                                fontWeight="800"
                                                fontSize="24"
                                                styles={
                                                    availability === ProductAvailability.outOfStock ? { lineThrough: true } : {}
                                                }>
                                                {formatPrice(price)}
                                            </Typography>
                                        </div>
                                    </>
                                ),
                            },
                            // hide 2nd and 3rd cells on mobile
                            {
                                classes: 'd-none d-md-table-cell align-content-start pt-5',
                                content: <ProductAmount quantity={quantity || 0} setQuantity={(value) => setQuantity(value)} />,
                            },
                            {
                                classes: `${styles['price-col']} d-none d-md-table-cell align-content-start pt-5`,
                                content: (
                                    <>
                                        <Typography
                                            classes={`${styles.price} pb-1 mb-0`}
                                            fontWeight="800"
                                            fontSize="24"
                                            align="right"
                                            styles={availability === ProductAvailability.outOfStock ? { lineThrough: true } : {}}>
                                            {formatPrice(price)}
                                        </Typography>
                                    </>
                                ),
                            },
                        ],
                    ]}
                />
            </Card>
        </div>
    );
};

interface ProductAmountProps {
    quantity: number;
    setQuantity: (value: number) => void;
}

const ProductAmount: FC<ProductAmountProps> = ({ quantity, setQuantity }) => {
    return <InputAmount min={0} value={quantity} onChange={(value) => value !== quantity && setQuantity(value)} size="sm" />;
};
